import type * as api from '@gitbook/api-client';

import type { AgentEditTarget } from '../AgentChat';
import type { AgentDockPageContext } from './state';

/**
 * Whether the target the location offers is the one the agent works in, or undefined while that
 * cannot be told yet.
 *
 * A space is its id. A change request is not: the location carries what the URL does, usually its
 * number, while the agent always reports its id — so unless the location happens to carry the id
 * too, the comparison waits on the attached change request being described by the API.
 */
export function isSameEditTarget(
    location: AgentEditTarget,
    attached: AgentEditTarget,
    attachedChangeRequest: Pick<api.ChangeRequest, 'id' | 'number'> | null | undefined
): boolean | undefined {
    if (location.spaceId !== attached.spaceId || location.kind !== attached.kind) {
        return false;
    }
    if (location.kind === 'space' || attached.kind === 'space') {
        return true;
    }
    if (location.changeRequest === attached.changeRequest) {
        return true;
    }
    if (!attachedChangeRequest) {
        return undefined;
    }
    return (
        attachedChangeRequest.id === String(location.changeRequest) ||
        String(attachedChangeRequest.number) === String(location.changeRequest)
    );
}

/**
 * Whether the page published to the dock is a page of the target.
 *
 * A change request is matched on whichever identifier the target carries — the URL's number, or the
 * id the agent reports — which is why the page publishes both. A space target matches a page with no
 * change request at all: that is a page edited live in the space, with nothing in between — while a
 * page of one of its change requests is a page of somewhere the target is not.
 */
export function isPageOfTarget(
    published: AgentDockPageContext,
    target: AgentEditTarget | null
): boolean {
    if (!target || published.spaceId !== target.spaceId) {
        return false;
    }
    if (target.kind === 'space') {
        return !published.changeRequest;
    }
    if (!published.changeRequest) {
        return false;
    }
    return typeof target.changeRequest === 'number'
        ? published.changeRequest.number === target.changeRequest
        : published.changeRequest.id === target.changeRequest;
}
